<template>
    <template v-if="renderStore.builderMode">
        <div class="rd-grid rd-gap-2 rd-w-full project-selection">
            <button class="rd-btn rd-btn-primary rd-mb-2" type="button">Project Selection</button>
        </div>
    </template>
    <template v-else>
        <template v-if="showDropdown">
            <section class="project-dropdown-container" :class="dropdownWrapperClass">
                <template v-if="fieldStyle != FieldStyle.Default">
                    <div class="form-field-container" :class="[{ 'form-underline': fieldStyle == FieldStyle.Underline },
                    { 'form-compact form-compact-full rd-w-full': fieldStyle == FieldStyle.Compact }]">
                        <label for="selectedProjects" :class="{ 'rd-sr-only': !hasLabel }">{{ getLabel }}</label>
                        <div class="rd-relative">
                            <VueMultiselect id="selectedProjects" v-model="selectedProjects" track-by="value"
                                label="text" select-label="" selected-label="" deselect-label=""
                                class="select-project multiselect-element" :class="[{ 'multiselect-underline': fieldStyle == FieldStyle.Underline },
                                { 'multiselect-compact': fieldStyle == FieldStyle.Compact }]"
                                :multiple="projectSettings.allowMultipleProjects"
                                :close-on-select="!projectSettings.allowMultipleProjects" :placeholder="getPlaceholder"
                                :options="dropdownProjects" :loading="projectsLoading" :searchable="true"
                                :internal-search="false" :allow-empty="true" @search-change="searchProjects"
                                @select="selectProject" @remove="removeSelect" @open="setDropdownWrapperClass('open')"
                                @close="setDropdownWrapperClass('close')" aria-autocomplete="list"
                                :aria-expanded="isProjectsOpen" aria-controls="listbox-selectedProjects"
                                aria-label="Select from dropdown list">
                            </VueMultiselect>
                            <span class="border-input-focus" v-if="fieldStyle == FieldStyle.Underline"></span>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="form-field-container rd-w-full">
                        <label for="selectedProjects" :class="{ 'rd-sr-only': !hasLabel }">{{ getLabel }}</label>
                        <VueMultiselect id="selectedProjects" v-model="selectedProjects" track-by="value" label="text"
                            select-label="" selected-label="" deselect-label=""
                            class="select-project multiselect-element" :multiple="projectSettings.allowMultipleProjects"
                            :close-on-select="!projectSettings.allowMultipleProjects" :placeholder="getPlaceholder"
                            :options="dropdownProjects" :loading="projectsLoading" :searchable="true"
                            :internal-search="false" :allow-empty="true" @search-change="searchProjects"
                            @select="selectProject" @remove="removeSelect" @open="setDropdownWrapperClass('open')"
                            @close="setDropdownWrapperClass('close')" aria-autocomplete="list"
                            :aria-expanded="isProjectsOpen" aria-controls="listbox-selectedProjects"
                            aria-label="Select from dropdown list">
                        </VueMultiselect>
                    </div>
                </template>
            </section>
        </template>
        <section class="projects-container" :class="{ 'chosen-projects': showDropdown }">
            <template v-if="allowsMultipleProjects && cardProjects.length >= 1 || cardProjects.length > 1">
                <ul>
                    <li v-for="project in cardProjects" :key="project.value" class="project">
                        <ProjectCardComponent :id="project.value" :showImage="projectSettings.showImage"
                            :showDescription="projectSettings.showDescription" @remove-project="removeProject">
                            <template #projectGiftArray>
                                <GiftArrayComponent v-if="showProjectGiftArray" :component="component"
                                    :projectId="project.value" :projectName="project.text"
                                    :useSmartGiftArray="useSmartGiftArray" :hasGiftArray="projectSettings.showGiftArray"
                                    :hasCustomAmount="projectSettings.showCustomAmount"
                                    :customAmountLabel="`Donation amount for ${project.text}`" />

                                <button v-else type="button" class="rd-btn rd-btn-primary rd-btn-select"
                                    :class="{ active: renderStore.isProjectActive(project.value) }"
                                    @click.stop="selectProject(project)">

                                    <i v-if="renderStore.isProjectActive(project.value)"
                                        class="fa-solid fa-check rd-me-1" aria-hidden="true"></i>

                                    <template v-if="renderStore.isProjectActive(project.value)">
                                        Selected <span v-if="isFixedAmount">(${{ component.customData.fixedAmount
                                        }})</span>
                                    </template>

                                    <template v-else>
                                        Select
                                    </template>
                                </button>
                            </template>
                        </ProjectCardComponent>
                    </li>
                </ul>
            </template>

            <template v-else-if="!allowsMultipleProjects && cardProjects.length === 1">
                <div v-for="project in cardProjects" :key="project.value"
                    class="project single-project rd-flex rd-mt-3.5">
                    <ProjectCardComponent :id="project.value" :showImage="projectSettings.showImage"
                        :showDescription="projectSettings.showDescription" @remove-project="removeProject">
                        <template #projectGiftArray>
                            <GiftArrayComponent v-if="showProjectGiftArray" :component="component"
                                :projectId="project.value" :projectName="project.text"
                                :useSmartGiftArray="useSmartGiftArray" :hasGiftArray="projectSettings.showGiftArray"
                                :hasCustomAmount="projectSettings.showCustomAmount" />

                            <button v-else type="button" class="rd-btn rd-btn-primary rd-btn-select"
                                :class="{ active: renderStore.isProjectActive(project.value) }"
                                @click.stop="selectProject(project)">

                                <i v-if="renderStore.isProjectActive(project.value)" class="fa-solid fa-check rd-me-1"
                                    aria-hidden="true"></i>

                                <template v-if="renderStore.isProjectActive(project.value)">
                                    Selected <span v-if="isFixedAmount">(${{ component.customData.fixedAmount }})</span>
                                </template>

                                <template v-else>
                                    Select
                                </template>
                            </button>
                        </template>
                    </ProjectCardComponent>
                </div>
            </template>
        </section>
    </template>
</template>

<script lang="ts">
import { ProjectResponseModel } from "@/builder/interfaces";
import { IDonationBlockComponent, PageDesign, ProjectSettings } from "@/builder/interfaces/index";
import { useRenderStore } from "@/builder/stores/renderStore";
import { Selection } from "@/enums";
import LookupResponse from "@/interfaces/responses/LookupResponse";
import { publicApiService } from "@/services/publicApiService";
import { computed, defineComponent, inject, PropType, Ref } from "vue";
import VueMultiselect from 'vue-multiselect';
import { FieldStyle, GiftArrayType, ProjectDisplayType } from "../../enums";
import GiftArrayComponent from "./GiftArrayComponent.vue";
import ProjectCardComponent from "./ProjectCardComponent.vue";
import { amountToNumber } from "@/utilities/NumberUtilities";

export default defineComponent({
    name: "ProjectContainerComponent",
    props: {
        component: {
            type: Object as PropType<IDonationBlockComponent>,
            required: true
        },
        useSmartGiftArray: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    components: {
        ProjectCardComponent,
        GiftArrayComponent,
        VueMultiselect
    },
    setup() {
        const renderStore = useRenderStore();

        const pageDesign = inject("pageDesign") as Ref<PageDesign>;

        const fieldStyle = computed(() => {
            return pageDesign.value.fieldStyle;
        });

        const publicApi = inject('publicApiService') as publicApiService;
        return { renderStore, publicApi, GiftArrayType, ProjectDisplayType, fieldStyle, FieldStyle };
    },
    data() {
        return {
            pinnedProjects: [] as LookupResponse[],
            availableProjects: [] as LookupResponse[],
            selectedProjects: [] as LookupResponse[],
            dropdownWrapperClass: '',
            projectSearchTimeout: null as any,
            projectsLoading: false,
            isProjectsOpen: false
        }
    },
    computed: {
        showProjectGiftArray() {
            return (this.projectSettings.showGiftArray ||
                this.projectSettings.showCustomAmount ||
                this.projectSettings.allowMultipleProjects) &&
                this.component.customData.giftArrayType !== GiftArrayType.FixedAmount;
        },

        cardProjects(): LookupResponse[] {
            let projects = new Array<LookupResponse>();
            if (this.projectSettings.displayType == ProjectDisplayType.Dropdown) return projects;

            switch (this.projectSettings.displayType) {
                case ProjectDisplayType.Card:
                    projects.push(...this.availableProjects);

                    //if showPinnedProjects is true or undefined, add pinned projects to the top of the list
                    if (this.projectSettings.showPinnedProjects || this.projectSettings.showPinnedProjects === undefined) {
                        projects = this.addPinnedProjects(projects);
                    }
                    break;
                case ProjectDisplayType.Dropdown_With_Card:
                    this.projectSettings.allowMultipleProjects
                        ? projects.push(...this.selectedProjects)
                        : projects.push(...(this.selectedProjects as any)?.value ? [this.selectedProjects as any] : this.selectedProjects);
                    break;
            }

            return projects;
        },

        dropdownProjects(): LookupResponse[] {
            let projects = new Array<LookupResponse>();

            projects.push(...this.availableProjects);

            //if showPinnedProjects is true or undefined, add pinned projects to the top of the list
            if (this.projectSettings.showPinnedProjects || this.projectSettings.showPinnedProjects === undefined) {
                projects = this.addPinnedProjects(projects);
            }

            return projects;
        },

        showDropdown() {
            if (this.projectSettings.projectSelection === Selection.All || this.projectSettings.projectSelection === Selection.Exclude) return true;
            if (!this.availableProjects.length || this.availableProjects.length === 1) return false;

            return this.projectSettings.displayType == ProjectDisplayType.Dropdown_With_Card ||
                this.projectSettings.displayType == ProjectDisplayType.Dropdown
        },

        hasSingleProject() {
            return this.availableProjects.length === 1;
        },

        projectSettings(): ProjectSettings {
            return this.component.customData.projectSettings;
        },

        defaultAmount(): string {
            //if gift array type is fixed amount, use fixed amount as they have no option to change amount
            if (this.component.customData.fixedAmount) return this.component.customData.fixedAmount.toString();

            //if multiple projects are allowed, use 0 as default amount
            if (this.projectSettings.allowMultipleProjects) return "0";

            return this.renderStore.amount.toString() ?? "0";
        },

        getPlaceholder(): string {
            return this.projectSettings.projectPlaceholder ? this.projectSettings.projectPlaceholder : "Select a project";
        },

        hasLabel(): boolean {
            return this.projectSettings.projectLabel ? true : false;
        },

        getLabel(): string {
            return this.projectSettings.projectLabel ? this.projectSettings.projectLabel : "Projects";
        },

        isFixedAmount() {
            return this.component.customData.giftArrayType == GiftArrayType.FixedAmount;
        },

        allowsMultipleProjects() {
            return this.projectSettings.allowMultipleProjects;
        }
    },
    methods: {
        searchProjects(search: string | null): void {
            if (this.projectSettings.projectSelection === Selection.Include) return;

            this.projectsLoading = true;
            clearTimeout(this.projectSearchTimeout);

            this.projectSearchTimeout = setTimeout(() => {
                this.publicApi.searchProjects(this.renderStore.organizationId, search)
                    .then((response) => {
                        if (this.projectSettings.projectSelection === Selection.Exclude) {
                            this.availableProjects = response.filter(x => !this.projectSettings.excludedProjects.find(y => y.value == x.value));
                        }
                        else {
                            this.availableProjects = [];
                            this.availableProjects.push(...response);
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                    })
                    .finally(() => {
                        this.projectsLoading = false;
                    });
            }, 300);
        },

        getPinnedProjects(): void {
            if (this.projectSettings.showPinnedProjects !== undefined && this.projectSettings.showPinnedProjects === false) return;

            this.publicApi.getPinnedProjects(this.renderStore.organizationId)
                .then((response) => {
                    //order pinned projects alphabetically
                    response.sort((a, b) => a.text.localeCompare(b.text))
                    this.pinnedProjects = response;
                })
                .catch((error) => {
                    console.log(error);
                });
        },

        addPinnedProjects(projects: LookupResponse[]): LookupResponse[] {
            //if pinned projects are already present in projects, remove them, they will be added to the top of the list
            projects = projects.filter(x => !this.pinnedProjects.find(y => y.value == x.value));
            projects.unshift(...this.pinnedProjects);
            return projects;
        },

        selectProject(project: LookupResponse): void {
            if (this.showProjectGiftArray) return;

            //if project is already selected, remove project (user is clicking card to remove project)
            if (this.renderStore.gift.projectSplits.find(x => x.projectId == project.value)) {
                if (Array.isArray(this.selectedProjects)) {
                    this.selectedProjects = this.selectedProjects.filter(x => x.value != project.value);
                }
                else {
                    this.selectedProjects = [];
                }

                this.removeProject(project.value);

                return;
            }

            this.renderStore.setAmount(
                this.defaultAmount,
                project.value,
                project.text,
                this.projectSettings.allowMultipleProjects);
        },

        removeProject(projectId: string): void {
            //remove project from selected projects
            if (this.projectSettings.displayType == ProjectDisplayType.Dropdown ||
                this.projectSettings.displayType == ProjectDisplayType.Dropdown_With_Card) {
                if (Array.isArray(this.selectedProjects)) {
                    this.selectedProjects = this.selectedProjects.filter(x => x.value != projectId);
                }
                else {
                    this.selectedProjects = [];
                }
            }

            this.renderStore.removeProject(projectId);
        },

        removeSelect(val: LookupResponse) {
            this.renderStore.removeProject(val.value);
        },

        setDropdownWrapperClass(action: string) {
            if (action == 'open') {
                this.dropdownWrapperClass = 'full-height';
                this.isProjectsOpen = true;
            }
            else {
                this.isProjectsOpen = false;

                setTimeout(() => {
                    this.dropdownWrapperClass = '';
                }, 125);
            }
        },

        handleProjectPreselect(preselectedProject: ProjectResponseModel) {
            if (this.availableProjects.findIndex(x => x.value == preselectedProject.id) == -1) {
                this.availableProjects.push(
                    {
                        value: preselectedProject.id.toString(),
                        text: preselectedProject.name,
                        metadata: {
                            code: preselectedProject.code,
                            name: preselectedProject.name,
                        }
                    }
                );
            }

            if (this.selectedProjects.findIndex(x => x.value == preselectedProject.id) == -1) {
                this.selectedProjects.push(
                    {
                        value: preselectedProject.id.toString(),
                        text: preselectedProject.name,
                        metadata: {
                            code: preselectedProject.code,
                            name: preselectedProject.name,
                        }
                    }
                );
            }

            this.renderStore.setAmount(
                amountToNumber(this.renderStore.gift.amount) > 0 ? this.renderStore.gift.amount ?? "0" : this.defaultAmount, //if amount is already set, use that amount (from url parameter)
                preselectedProject.id.toString(),
                preselectedProject.name,
                this.projectSettings.allowMultipleProjects ?? false);
        }
    },
    mounted() {
        if (this.renderStore.builderMode) return;

        if (this.projectSettings.showPinnedProjects || this.projectSettings.showPinnedProjects === undefined) {
            this.getPinnedProjects();
        }

        if (this.projectSettings.projectSelection === undefined || this.projectSettings.projectSelection === Selection.Include) {
            this.availableProjects = this.projectSettings.availableProjects; //map to local availableProjects so we can push item to it
        }
        else {
            this.availableProjects = [];
        }

        if (this.projectSettings.projectSelection === Selection.All || this.projectSettings.projectSelection === Selection.Exclude) {
            this.searchProjects(null);
        }

        //map project splits from store to selectedProjects
        if (this.renderStore.gift.projectSplits && this.renderStore.gift.projectSplits.length > 0) {
            if (this.projectSettings.displayType == ProjectDisplayType.Dropdown ||
                this.projectSettings.displayType == ProjectDisplayType.Dropdown_With_Card) {
                this.selectedProjects = this.renderStore.gift.projectSplits.map(x => {
                    return {
                        value: x.projectId,
                        text: this.availableProjects.find(y => y.value == x.projectId)?.text,
                        metadata: null
                    } as LookupResponse;
                });
            }
        }

        //if amount is already set on mount, and renderStore has no project splits, add split to first project
        if (this.renderStore.amount > 0 && this.renderStore.gift.projectSplits.length == 0) {
            this.renderStore.setAmount(
                this.defaultAmount,
                this.availableProjects[0].value,
                this.availableProjects[0].text,
                this.projectSettings.allowMultipleProjects);
        }
    },
    watch: {
        selectedProjects: function (val) {
            if (Array.isArray(val)) {
                for (let i = 0; i < val.length; i++) {
                    //check if project is already in the store project split
                    const projectSplit = this.renderStore.gift.projectSplits.find(x => x.projectId == val[i].value);
                    if (!projectSplit) {
                        this.renderStore.setAmount(
                            this.defaultAmount,
                            val[i].value,
                            val[i].text,
                            this.projectSettings.allowMultipleProjects);
                    }
                }
            }
            else if (val) {
                //check if project is already in the store project split
                const projectSplit = this.renderStore.gift.projectSplits.find(x => x.projectId == val.value);
                if (!projectSplit) {
                    this.renderStore.setAmount(
                        this.defaultAmount,
                        val.value,
                        val.text,
                        this.projectSettings.allowMultipleProjects);
                }
            }
        },
        'renderStore.page.projectPreselect': {
            handler(projectPreselect: ProjectResponseModel | null) {
                if (projectPreselect) {
                    this.handleProjectPreselect(projectPreselect);
                }
            },
            immediate: true,
        }
    }
});
</script>
